/**
 * TraitMap.js
 * Central definition for Adjective-to-Math mappings.
 * Ensures that descriptive tags result in concrete signal processing changes.
 */

export const TRAIT_MAP = {
    // --- Energy / Temperament ---
    agitated: {
        velocityVariance: 1.5, // 50% more variance
        duration: 0.8, // Staccato tendency
        timingRandomness: 1.5, // Jittery
        dynamics: 1.2 // Louder base
    },
    mellow: {
        velocityVariance: 0.6,
        duration: 1.1, // Legato
        timingRandomness: 0.8,
        dynamics: 0.8 // Softer
    },
    precise: {
        velocityVariance: 0.2,
        duration: 1.0,
        timingRandomness: 0.1, // Robotic
        dynamics: 1.0
    },
    balanced: {
        velocityVariance: 1.0,
        duration: 1.0,
        timingRandomness: 1.0,
        dynamics: 1.0
    },

    // --- Groove Character ---
    lazy: {
        timingOffset: 0.02, // Drag 20ms
        tightness: 0.6
    },
    rushed: {
        timingOffset: -0.015, // Rush 15ms
        tightness: 0.7
    },
    tight: {
        tightness: 0.95,
        timingRandomness: 0.3
    },
    loose: {
        tightness: 0.4,
        timingRandomness: 1.5
    },

    // --- Articulation ---
    aggressive: {
        velocityBase: 0.9,
        duration: 0.9,
        accentStrength: 1.3
    },
    soft: {
        velocityBase: 0.5,
        duration: 1.05,
        accentStrength: 0.7
    }
};

/**
 * Resolves a list of tags/traits into a unified modifier object.
 * @param {Array<string>} tags - List of adjectives (e.g. ['agitated', 'loose'])
 * @returns {Object} Combined modifiers
 */
export const resolveTraits = (tags = []) => {
    const combined = {
        velocityVariance: 1.0,
        duration: 1.0,
        timingRandomness: 1.0,
        dynamics: 1.0,
        tightness: 0.5, // Default
        timingOffset: 0.0,
        velocityBase: 1.0
    };

    tags.forEach(tag => {
        const key = tag.toLowerCase();
        const mapping = TRAIT_MAP[key];
        if (mapping) {
            if (mapping.velocityVariance) combined.velocityVariance *= mapping.velocityVariance;
            if (mapping.duration) combined.duration *= mapping.duration;
            if (mapping.timingRandomness) combined.timingRandomness *= mapping.timingRandomness;
            if (mapping.dynamics) combined.dynamics *= mapping.dynamics;

            // Non-multiplicative overrides (last wins or average?)
            if (mapping.tightness !== undefined) combined.tightness = mapping.tightness;
            if (mapping.timingOffset !== undefined) combined.timingOffset += mapping.timingOffset; // Additive offsets
            if (mapping.velocityBase !== undefined) combined.velocityBase = mapping.velocityBase;
        }
    });

    return combined;
};
